// FTO-10 — a filter's NAME is a first-class object.
//
// "filter by category" names the Category FIELD, but Stage B grounds every phrase as a VALUE: the resolver
// only ever sees option candidates (no field labels), so the literal word "category" fuzzy/embeds against
// option names across ~70 fields and the card leads with nonsense ("Sounds like you might mean 'system'
// (Recommendation Type)" — trace 1ae06a69, fb 365). This module makes the field-name reading exist:
//
//   - detectFieldMentions: does an interpreter phrase EXACTLY name a browsable field's label?
//   - valueConflicts:      is that word ALSO a real VALUE in a DIFFERENT field — literally an option's name
//                          ("system" is the System field AND a Recommendation Type option at method
//                          "exact"/1.0), or contained in one ("compliance" ⊂ category "Code Compliance")?
//                          Either is a genuine two-reading conflict the card must keep offering.
//   - buildBrowseResult:   a synthesized resolution result whose suggestions are the field's own options,
//                          so the EXISTING card renders it (multi pills, nothing preselected, feasibility
//                          counts, Apply) with no new card component.
//
// Stability levers, deliberate:
//   - EXACT normalized label equality only. Partial/loose containment ("recommendation" inside
//     "Recommendation Type" — the crossCapabilityCatalog `filterMatchMode` "loose" class) never fires, so
//     every existing utterance keeps today's behavior byte-for-byte.
//   - A conflict needs `method === "exact"` or the resolver's `subset` flag. A BARE fuzzy 1.0 is still not a
//     value reading — token_set_ratio scores the token INTERSECTION, so "sub system" ties "system" at 1.0
//     without the user meaning it, while `subset` says the option's name really does contain the word.
//   - Boolean fields are excluded: their label IS the concept and the boolean path already resolves the
//     bare field name to Yes (booleanValuePhrase) — gating them would regress "show me immediate".
//
// The orchestrator injects the browse result AFTER pruneWeakSuggestions (score-less pills would fail the
// floor) and composes per `valueConflicts`: no conflict -> the browse result REPLACES the phrase's value
// noise (user decision: straight to browse); conflict -> browse first, the surviving value matches kept
// below — the combined card IS the clarification, both readings tappable.

// How many option pills a browse card shows before switching to "give me a word and I'll narrow" (+ the
// open-the-filter-window escape hatch). 12 covers most bounded fields whole (category/trade/criticality/
// region/building type); sub_system (218) and manufacturer (195) page. User decision 2026-07-30.
export const BROWSE_PAGE_SIZE = 12;

// Ref prefix for synthesized browse results. buildApplyPayload treats any non-`c:` ref as action "set",
// so browse picks apply exactly like ambiguous-term picks.
export const BROWSE_REF_PREFIX = "b:";

// Local normalizer — deliberately NOT navCatalog.normalizeText (that module imports checkPermission at
// load, which would drag permission mocks into every orchestrator test). Lowercase, non-alphanumerics to
// spaces, then a naive per-token plural fold ("categories" -> "category", "trades" -> "trade"). The fold
// is applied to BOTH sides, so systematic distortions ("status" -> "statu") stay symmetric and harmless.
const foldToken = t => {
    if (t.length > 4 && t.endsWith("ies")) return `${t.slice(0, -3)}y`;
    if (t.length > 3 && t.endsWith("s") && !t.endsWith("ss")) return t.slice(0, -1);
    return t;
};
export const normalizeLabel = text =>
    String(text || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map(foldToken)
        .join(" ");

// Filler that carries no field information in a "filter by X"-shaped utterance. Deliberately small and
// verb/politeness-only — a CONTENT word must never be stripped, or the fallback below could misread
// "filter by category energy" as a bare Category mention.
const UTTERANCE_FILLER = new Set([
    "i", "we", "you", "me", "my", "our", "us", "the", "a", "an", "it", "that", "this", "these", "those",
    "want", "wants", "wanted", "would", "like", "need", "needs", "to", "please", "can", "could", "lets",
    "let", "filter", "filters", "filtering", "filtered", "by", "on", "for", "show", "see", "view", "set",
    "use", "apply", "add", "do", "go",
    // meta-language about a field's contents, not a value ("category options"). Tokens are checked AFTER
    // normalizeLabel's plural fold, so the folded forms are what must be listed.
    "option"
]);

// The phrases a mention can live in: every ambiguous term, plus every SET-action committed value phrase
// (the interpreter sometimes commits the label word as a value — the cross-field-topics class). Each entry
// carries the resolve ref it would have grounded under, so the gate can find/replace that exact result.
//
// FALLBACK SOURCE — the value-less field ask (found on Dev3, trace b49814ad): "i want to filter by
// category" makes the interpreter COMMIT to nothing and ask its own question ("Which category…?") with an
// EMPTY intent — no ambiguous term, no value phrase — so the gate had no source, zero results rendered,
// and Otto fell back to "could you add a little more detail?". When the intent is empty-but-clarifying
// like that, the UTTERANCE itself is the source: strip filler, and if what remains exactly names a field
// (same normalize/fold as every other mention), that's the mention (ref "u:0" — nothing to replace, the
// gate appends). Gated hard on the intent carrying NOTHING groundable, so any utterance with a real value
// or filter keeps today's behavior untouched.
const mentionSources = (intent, utterance) => {
    const sources = [];
    (intent.ambiguous_terms || []).forEach((at, i) => {
        if (at && at.phrase) sources.push({ phrase: at.phrase, ref: `a:${i}` });
    });
    (intent.common_filters || []).forEach((cf, i) => {
        if (((cf && cf.action) || "set") !== "set") return; // add/remove refinements are not mentions
        ((cf && cf.value_phrases) || []).forEach((phrase, j) => {
            if (phrase) sources.push({ phrase, ref: `c:${i}:${j}` });
        });
    });
    if (sources.length || !utterance || !intent.needs_clarification) return sources;
    const emptyIntent =
        !(intent.common_filters || []).length &&
        !(intent.wildcard_filters || []).length &&
        !(intent.clears || []).length &&
        !intent.clear_all &&
        !intent.search &&
        !intent.year_range;
    if (!emptyIntent) return sources;
    const leftover = normalizeLabel(utterance)
        .split(" ")
        .filter(t => t && !UTTERANCE_FILLER.has(t))
        .join(" ");
    if (leftover) sources.push({ phrase: leftover, ref: "u:0" });
    return sources;
};

// How many numeric fields a loose wildcard mention may offer prompt pills for ("cost" names several cost
// fields — that ambiguity IS the clarify). Shortest labels first: closest to the bare mention.
export const THRESHOLD_MAX_FIELDS = 6;

const tokensOf = label => normalizeLabel(label).split(" ").filter(Boolean);
const containsAllTokens = (labelTokens, phraseTokens) => phraseTokens.every(t => labelTokens.includes(t));

// Detect phrases that name a field the gate can act on. `isBrowseable` is the orchestrator's
// isControlledVocabulary predicate, passed in (not imported) to avoid a module cycle. Returns
// [{ phrase, ref, kind, meta | metas }] — at most one mention per phrase:
//   kind "dimension" -> EXACT label match on a browsable controlled vocabulary (FTO-10: the browse card).
//   kind "wildcard"  -> numeric/date wildcard(s) (FTO-12: the threshold prompt). Exact label match, OR
//                       loose TOKEN CONTAINMENT ("cost" ⊆ "Construction Cost" / "Project Cost" — several
//                       fields may match, and that ambiguity is exactly what the card clarifies, one
//                       prompt pill per field). `metas` is the matched list, shortest labels first.
// Stability guards, deliberate: dimensions stay EXACT-only (FTO-10's lever); the loose wildcard pass
// stands down when ANY dimension label token-contains the phrase — "fci" is contained in the numeric
// "Building FCI" wildcard AND the "Building FCI Description" band dimension, and the band flows own those
// phrases today. Text wildcards (description/notes) keep today's behavior in every case.
export const detectFieldMentions = (intent, maps, isBrowseable, utterance) => {
    if (!intent || !maps || !maps.dimMap || typeof isBrowseable !== "function") return [];
    const wildcards = Object.values(maps.wildcardMap || {}).filter(
        m => m && m.label && (m.value_type === "number" || m.value_type === "date")
    );
    const byLabel = new Map();
    wildcards.forEach(meta => {
        const key = normalizeLabel(meta.label);
        if (key && !byLabel.has(key)) byLabel.set(key, { meta, kind: "wildcard" });
    });
    Object.values(maps.dimMap).forEach(meta => {
        if (!meta || !meta.label || meta.isBoolean) return;
        if (meta.value_kind === "text" || meta.id === "search") return;
        if (!isBrowseable(meta)) return; // nothing enumerable to browse (high-cardinality id dims)
        const key = normalizeLabel(meta.label);
        if (key) byLabel.set(key, { meta, kind: "dimension" }); // dimensions override wildcard collisions
    });
    const dimTokenLists = Object.values(maps.dimMap)
        .filter(m => m && m.label)
        .map(m => tokensOf(m.label));
    const seen = new Set();
    const mentions = [];
    mentionSources(intent, utterance).forEach(({ phrase, ref }) => {
        const key = normalizeLabel(phrase);
        if (!key || seen.has(key)) return;
        const exact = byLabel.get(key);
        if (exact) {
            seen.add(key);
            mentions.push(
                exact.kind === "dimension"
                    ? { phrase, ref, kind: "dimension", meta: exact.meta }
                    : { phrase, ref, kind: "wildcard", metas: [exact.meta] }
            );
            return;
        }
        // Loose pass — numeric/date wildcards only, and only when no dimension label owns the tokens.
        const phraseTokens = key.split(" ");
        if (dimTokenLists.some(lt => containsAllTokens(lt, phraseTokens))) return;
        const loose = wildcards
            .filter(m => containsAllTokens(tokensOf(m.label), phraseTokens))
            .sort((a, b) => a.label.length - b.label.length)
            .slice(0, THRESHOLD_MAX_FIELDS);
        if (loose.length) {
            seen.add(key);
            mentions.push({ phrase, ref, kind: "wildcard", metas: loose });
        }
    });
    return mentions;
};

// The surviving suggestions that make the word a REAL value reading somewhere else, on a field OTHER than the
// named one. Two signals, both from the resolver:
//   - method "exact"  — the word IS that option's name ("system" IS a Recommendation Type option).
//   - `subset`        — the option's name CONTAINS the word, plus at most one more word ("compliance" ⊂
//                       category "Code Compliance"). See `_is_contained` in filter-ai/app/core/resolver.py.
// Everything weaker stays noise: a bare fuzzy tie (`token_set_ratio` scores the token INTERSECTION, so
// "system" ties sub_system's "Building Automation System (BAS) Controls" at 1.0 without the user meaning it)
// and embedding echoes are what made the browse card necessary in the first place.
//
// Why `subset` had to become a signal: "show me compliance issues" resolved category "Code Compliance" at
// fuzzy 1.0 — a reading the user genuinely wanted — and the exact-only test read it as noise, so the browse
// card REPLACED it and Otto offered the Compliance priority element's two options only (trace 654154d7).
// The same phrase spelled "code compliance" hits the category as `exact` and keeps it, so the exact-only
// test made Otto's answer depend on whether the user happened to say the option's full name.
export const valueConflicts = (result, fieldId) =>
    (((result && result.suggestions) || []).filter(
        s => s && s.field !== fieldId && (s.method === "exact" || s.subset === true)
    ));

// Synthesize the THRESHOLD result for a numeric/date field mention (FTO-12). Carries no suggestions —
// the interactive surface is one bare `prompt` wildcard pill PER matched field (the pill's editor) — but
// it IS a `clarify` decision, so the card asks, the refine hint shows, and a plan pauses on it like any
// other question. ResultBlock renders nothing for it; the presenter's headline speaks for the whole card.
export const buildThresholdResult = (metas, { ref = "t:0", phrase } = {}) => ({
    ref,
    phrase: phrase || (metas[0] && metas[0].label),
    decision: "clarify",
    multi: false,
    chosen: null,
    threshold: {
        phrase,
        fields: metas.map(m => ({ field: m.id, label: m.label, value_type: m.value_type || "number" }))
    },
    suggestions: []
});

// The prompt wildcard(s) the threshold card renders: value-less, flagged so the pill's body-tap opens the
// EDITOR instead of toggling — selecting a bare wildcard would otherwise apply as a CLEAR of the field
// (buildApplyPayload's operator-without-value rule), which is the opposite of what the user asked for.
export const buildPromptWildcard = meta => ({
    field: meta.id,
    operator: "gt",
    value: null,
    value_range: null,
    prompt: true
});

// The typed-narrowing decision for an open browse question, pure and synchronous: `hits` are the
// options whose name contains the text (the narrowed page); `exact` means the text IS one option typed in
// full — a pick, not a narrowing. Zero hits means the text wasn't about this field at all, and the caller
// falls through to the cold pipeline ("go to buildings" while a browse card is open still navigates).
export const narrowOptions = (optionsAll, text) => {
    const needle = String(text || "").trim().toLowerCase();
    if (!needle) return { hits: [], exact: false };
    const hits = (optionsAll || []).filter(o => String(o.name).toLowerCase().includes(needle));
    const exact = hits.length === 1 && normalizeLabel(hits[0].name) === normalizeLabel(text);
    return { hits, exact };
};

// Synthesize the browse result the existing card renders. Shape mirrors a real resolver result: the multi
// branch shows plain name pills (no scores), nothing preselected, and Apply flows through buildApplyPayload
// (the `b:` ref defaults to action "set"). `browse` carries what the card + question protocol need.
export const buildBrowseResult = (meta, options, { ref = `${BROWSE_REF_PREFIX}0`, shown = BROWSE_PAGE_SIZE, narrowWord = null, phrase } = {}) => {
    const all = options || [];
    const page = all.slice(0, shown);
    return {
        ref,
        phrase: phrase || meta.label,
        decision: "clarify",
        multi: true,
        chosen: null,
        browse: {
            field: meta.id,
            label: meta.label,
            value_kind: meta.value_kind || "id",
            total: all.length,
            shown: page.length,
            narrowWord: narrowWord || null,
            // The FULL vocabulary rides along (id+name only) so the typed-narrowing loop is a synchronous
            // substring filter — no async in the answer path, and a word matching nothing falls through to
            // the cold pipeline like any other new request. Controlled vocabularies are bounded (largest
            // seen: sub_system 218), so this stays a few KB in the chat/question stores.
            options_all: all.map(o => ({ id: o.id != null ? o.id : o.name, name: o.name }))
        },
        suggestions: page.map(o => ({
            field: meta.id,
            id: o.id != null ? o.id : o.name, // value-kind options carry the value as both
            name: o.name,
            value_kind: meta.value_kind || "id"
        }))
    };
};
